import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Megaphone, Wrench, RefreshCw } from "lucide-react";
import {
  getStatusColor,
  getCategoryLabel,
  getMediaTypeLabel,
  type WaTemplateCategory,
  type WaTemplateMediaType,
  type WaTemplateButton,
  type WaTemplateLanguage,
} from "@/data/whatsappTemplateMocks";
import { useWaTemplates } from "@/hooks/useWaTemplates";
import { TemplateForm } from "./wa-templates/TemplateForm";
import { WhatsAppPreview } from "./wa-templates/WhatsAppPreview";
import { SettingsCard } from "./SettingsCard";
import { formatDateInAccountTz } from "@/lib/accountTimezone";
import { useTranslation } from "react-i18next";

interface FormData {
  name: string;
  category: WaTemplateCategory;
  language: WaTemplateLanguage;
  mediaType: WaTemplateMediaType;
  mediaPreviewUrl?: string;
  headerText?: string;
  body: string;
  footer?: string;
  buttons: WaTemplateButton[];
  bodyExamples: string[];
}

const emptyForm: FormData = {
  name: '',
  category: 'UTILITY',
  language: 'pt_BR',
  mediaType: 'none',
  body: '',
  footer: '',
  buttons: [],
  bodyExamples: [],
};

const WhatsAppTemplatesTab = () => {
  const { t } = useTranslation("settings");
  const { templates, loading, syncing, error, sync, createTemplate } = useWaTemplates();
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [filterCategory, setFilterCategory] = useState<'ALL' | WaTemplateCategory>('ALL');

  // O preview usa os exemplos reais que serão enviados à Meta, então o que
  // aparece na tela é exatamente o que o modelo vai renderizar.
  const variableValues = useMemo(() => {
    const values: Record<string, string> = {};
    formData.bodyExamples.forEach((example, index) => {
      if (example?.trim()) values[`{{${index + 1}}}`] = example.trim();
    });
    return values;
  }, [formData.bodyExamples]);

  const filtered = templates.filter(template => {
    const matchSearch = template.name.toLowerCase().includes(search.toLowerCase()) ||
      template.body.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === 'ALL' || template.category === filterCategory;
    return matchSearch && matchCategory;
  });

  const handleCreate = async () => {
    setSubmitting(true);
    setFormError(null);

    const result = await createTemplate({
      name: formData.name,
      category: formData.category,
      language: formData.language,
      headerText: formData.headerText,
      body: formData.body,
      footer: formData.footer,
      buttons: formData.buttons,
      bodyExamples: formData.bodyExamples,
    });

    setSubmitting(false);

    if (!result.ok) {
      setFormError(result.error ?? t("waTemplates.createError"));
      return;
    }

    setFormData(emptyForm);
    setIsCreating(false);
  };

  if (isCreating) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setIsCreating(false); setFormData(emptyForm); setFormError(null); }}
            className="text-[#64748B] hover:text-[#2563EB] hover:bg-[#F1F5F9] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#60A5FA] dark:hover:bg-[#27272A]"
          >
            {t("waTemplates.back")}
          </Button>
          <h3 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.newTitle")}</h3>
        </div>

        {formError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
          <SettingsCard className="p-5">
            <TemplateForm
              data={formData}
              onChange={setFormData}
              onSubmit={handleCreate}
              onCancel={() => { setIsCreating(false); setFormData(emptyForm); setFormError(null); }}
              submitting={submitting}
            />
          </SettingsCard>

          <div className="hidden xl:flex flex-col items-center gap-3">
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide dark:text-[#71717A]">{t("waTemplates.preview")}</p>
            <WhatsAppPreview
              body={formData.body}
              footer={formData.footer}
              mediaType={formData.mediaType}
              mediaPreviewUrl={formData.mediaPreviewUrl}
              headerText={formData.headerText}
              buttons={formData.buttons}
              variableValues={variableValues}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] dark:text-[#A1A1AA]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("waTemplates.search")}
              className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] pl-9 focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] dark:focus-visible:ring-[#2563EB]/30"
            />
          </div>
        </div>

        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex rounded-xl p-0.5 border border-[#E5E7EB] bg-white dark:border-[#3F3F46] dark:bg-[#27272A]">
            {(['ALL', 'UTILITY', 'MARKETING'] as const).map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-200 ${
                  filterCategory === cat
                    ? 'bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]'
                    : 'text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB] dark:text-[#A1A1AA] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]'
                }`}
              >
                {cat === 'ALL' ? t('waTemplates.allCategories') : getCategoryLabel(cat)}
              </button>
            ))}
          </div>

          <Button
            variant="outline"
            onClick={() => sync()}
            disabled={syncing}
            className="rounded-xl border border-[#E5E7EB] bg-white px-4 text-[#334155] gap-1.5 transition-colors duration-200 hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? t("waTemplates.syncing") : t("waTemplates.sync")}
          </Button>

          <Button
            onClick={() => setIsCreating(true)}
            className="rounded-xl bg-[#2563EB] px-4 text-white shadow-sm gap-1.5 transition-colors duration-200 hover:bg-[#1D4ED8] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
          >
            <Plus className="w-4 h-4" />
            {t("waTemplates.new")}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EEF4FF] flex items-center justify-center mb-4 dark:bg-blue-500/15">
            <Search className="w-6 h-6 text-[#2563EB] dark:text-[#60A5FA]" />
          </div>
          <p className="text-[#0F172A] font-medium mb-1 dark:text-[#FAFAFA]">{t("waTemplates.empty")}</p>
          <p className="text-[#64748B] text-sm mb-4 dark:text-[#71717A]">{t("waTemplates.emptyHint")}</p>
          <Button
            onClick={() => setIsCreating(true)}
            className="rounded-xl bg-[#2563EB] text-white shadow-sm gap-1.5 transition-colors duration-200 hover:bg-[#1D4ED8]"
          >
            <Plus className="w-4 h-4" />
            {t("waTemplates.new")}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(template => {
            const status = getStatusColor(template.status);
            return (
              <SettingsCard
                key={template.id}
                className="flex items-center gap-4 p-4 hover:bg-[#F8FAFC] hover:shadow-md transition-colors duration-200 dark:hover:bg-[#3F3F46]"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  template.category === 'MARKETING'
                    ? 'bg-[#EEF4FF] dark:bg-blue-500/15'
                    : 'bg-[#ECFDF5] dark:bg-green-500/15'
                }`}>
                  {template.category === 'MARKETING'
                    ? <Megaphone className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA]" />
                    : <Wrench className="w-4 h-4 text-[#16A34A] dark:text-[#22C55E]" />
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <p className="text-sm font-medium text-[#0F172A] font-mono dark:text-[#FAFAFA]">{template.name}</p>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                      {status.label}
                    </span>
                  </div>
                  <p className="text-xs text-[#64748B] line-clamp-1 dark:text-[#71717A]">{template.body}</p>
                </div>

                <div className="hidden md:flex items-center gap-4 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-xs text-[#64748B] dark:text-[#71717A]">{getCategoryLabel(template.category)}</p>
                    <p className="text-xs text-[#94A3B8] dark:text-[#71717A]">{getMediaTypeLabel(template.mediaType)}</p>
                  </div>
                  <div className="text-xs text-[#64748B] dark:text-[#71717A]">
                    {formatDateInAccountTz(template.createdAt)}
                  </div>
                </div>
              </SettingsCard>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplatesTab;
